"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.multiDispatchEventFactory = exports.multiConsumeSubjectFactory = exports.positionTriggersProviderFactory = void 0;
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const trade_utils_lib_1 = require("trade_utils_lib");
const TPSLUtils_1 = require("./../../../component/Trade/FutureTrade/FuturesTPSL/TPSLUtils");
const alwaysTrue = () => true;
function formatTriggerPrice(order) {
    // 格式化一下触发价格，避免显示为科学计数
    if (order.trigger_price) {
        order.trigger_price = new trade_utils_lib_1.SafeDecimal(order.trigger_price).toFixed();
    }
    return order;
}
/**
 * 工厂函数：用来生成管理仓位关联的止盈止损的实例
 *
 * 该实例在内存维护多个map，来缓存仓位关联的`条件单`(包括仓位关联的止盈止损以及仓位关联的追踪出场)。
 *
 * 注意：一个仓位可以关联`多对`止盈止损以及`单个`追踪出场
 *
 * @param
 * - query 通过仓位id列表，获取仓位关联的止盈止损以及追踪出场
 * - notifyDataChanged 通知外部仓位关联的止盈止损数据已经更新
 * - dataKey 当更新(新增或删除)条件单时，会判断dataKey对应的字段是否存在，如果存在才进行更新操作
 * - willInsertItem 当插入数据到缓存时，将回调该函数；可以自定义该函数来控制是否更新条件单到缓存中
 */
function positionTriggersProviderFactory(params) {
    const { query, notifyDataChanged, dataKey, willInsertItem = alwaysTrue } = params;
    // 记录已经获取过止盈止损数据的仓位id
    const cachedIdSet = new Set();
    // 记录仓位关联的止盈列表， key为仓位的id
    const tpOrderMap = new Map();
    // 记录仓位关联的止损列表， key为仓位的id
    const slOrderMap = new Map();
    // 记录仓位关联的追踪出场订单
    const trackOrderMap = new Map();
    // loading状态，表明是否正在获取数据
    let loading = true;
    // refreshing状态，表明是否正在刷新
    let isRefreshing = false;
    // 缓存当前请求的subject
    let lastSub;
    function cleanAllData() {
        // clean
        cachedIdSet.clear();
        tpOrderMap.clear();
        slOrderMap.clear();
        trackOrderMap.clear();
    }
    // 过滤掉已经被缓存的id
    function filterCachedIds(sourceIds) {
        return sourceIds.filter((id) => !cachedIdSet.has(id));
    }
    // 将id新增到缓存中
    function addIdsToCache(ids) {
        ids.forEach((id) => cachedIdSet.add(id));
    }
    // 新增条件单到缓存中
    function insertNewOrder(it) {
        const key = it[dataKey] || "";
        // 由于后端数据问题，必须判断key的长度大于1才能证明这个值存在
        if (key.length > 1 && willInsertItem(it)) {
            // 获取条件单止盈止损的模式
            const mode = TPSLUtils_1.TPSLUtils.getOrderTPSLMode(formatTriggerPrice(it));
            // 根据不同的模式，更新数据到对应的map
            const map = mode === "tp" ? tpOrderMap : slOrderMap;
            // 避免重复添加同一个条件单
            const orderList = (map.get(key) || []).filter((o) => it.trigger_id !== o.trigger_id).concat(it);
            map.set(key, orderList);
        }
    }
    // 新增追踪出场订单到缓存中
    function insertNewTrackOrder(it) {
        const key = it[dataKey] || "";
        // 由于后端数据问题，必须判断key的长度大于1才能证明这个值存在
        if (key.length > 1 && willInsertItem(it)) {
            trackOrderMap.set(key, it);
        }
    }
    function batchInsertOrder(orders, tracks) {
        orders.forEach(insertNewOrder);
        tracks.forEach(insertNewTrackOrder);
    }
    function splitOrders(orders) {
        // 需要将获取到的数据拆分成止盈止损跟追踪回撤两个部分
        const tpslList = [];
        const trackList = [];
        (orders || []).forEach((it) => {
            if (TPSLUtils_1.TPSLUtils.isTPSLOrder(it)) {
                tpslList.push(it);
            }
            else {
                trackList.push(it);
            }
        });
        return [tpslList, trackList];
    }
    // 通过仓位id，批量获取仓位关联的止盈止损以及追踪出场的订单
    function queryPositionTriggers(positionIds, refresh = false) {
        return query(positionIds)
            .pipe((0, operators_1.map)(splitOrders))
            .subscribe({
            next: ([orders, tracks]) => {
                loading = false;
                if (refresh) {
                    // reset
                    cleanAllData();
                }
                // 批量插入获取到的止盈止损以及追踪出场的订单
                batchInsertOrder(orders, tracks);
                // 记录已经处理过的仓位id
                addIdsToCache(positionIds);
                // 通知外部缓存已经更新
                notifyDataChanged();
                isRefreshing = false;
            },
            error: (err) => {
                loading = false;
                isRefreshing = false;
            }
        });
    }
    function batchQueryPositionTriggers(positionIds, refresh = false) {
        if (lastSub) {
            lastSub.unsubscribe();
        }
        lastSub = queryPositionTriggers(positionIds, refresh);
    }
    /**
     * 该函数接受仓位id的列表或者条件单列表，并将满足条件的条件单更新到对应的止盈止损或者追踪出场的map中
     *
     * - orders非空时，直接更新传入的order到对应的map中
     * - 当ids非空时，则根据传入的id去获取关联的止盈止损或者追踪出场，并更新到对应的map中
     *
     * @param ids 仓位id列表
     * @param orders 待插入的条件单列表
     */
    function update(ids, orders) {
        if (orders && orders.length) {
            const [tpslList, trackList] = splitOrders(orders);
            tpslList.forEach(insertNewOrder);
            trackList.forEach(insertNewTrackOrder);
            addIdsToCache(ids);
            notifyDataChanged();
            return;
        }
        if (!ids.length) {
            // 避免一直处于loading
            loading = false;
        }
        const noCachedIds = filterCachedIds(ids);
        if (!noCachedIds.length) {
            return;
        }
        // 查询未被缓存的id的止盈止损单
        batchQueryPositionTriggers(noCachedIds);
    }
    /**
     * 移除ids关联的止盈止损单，或者直接移除orders传入的条件单
     * @param ids 仓位id列表
     * @param orders 仓位订单列表
     */
    function remove(ids, orders) {
        if (ids.length) {
            ids.forEach((id) => {
                tpOrderMap.delete(id);
                slOrderMap.delete(id);
                trackOrderMap.delete(id);
                cachedIdSet.delete(id);
            });
        }
        else {
            if (orders && orders.length) {
                orders.forEach((it) => {
                    // 由于后端数据问题，必须判断key的长度大于1才能证明这个值存在
                    const key = it[dataKey] || "";
                    if (key.length < 2) {
                        return;
                    }
                    // 判断是否是止盈止损单
                    if (TPSLUtils_1.TPSLUtils.isTPSLOrder(it)) {
                        const mode = TPSLUtils_1.TPSLUtils.getOrderTPSLMode(it);
                        const map = mode === "tp" ? tpOrderMap : slOrderMap;
                        const orderList = map.get(key);
                        if (!orderList) {
                            return;
                        }
                        map.set(key, orderList.filter((o) => o.trigger_id !== it.trigger_id));
                    }
                    else {
                        // 说明是追踪出场单
                        trackOrderMap.delete(key);
                    }
                });
            }
        }
        notifyDataChanged();
    }
    function isLoading() {
        return loading;
    }
    function refresh() {
        // 去重
        if (isRefreshing || !cachedIdSet.size) {
            return;
        }
        isRefreshing = true;
        queryPositionTriggers([...cachedIdSet], true);
    }
    function getTrackMap() {
        return trackOrderMap;
    }
    return {
        update,
        tpOrderMap,
        slOrderMap,
        remove,
        cleanAllData,
        isLoading,
        refresh,
        getTrackMap,
    };
}
exports.positionTriggersProviderFactory = positionTriggersProviderFactory;
/**工厂函数，生成用来监听的subject以及用来通知数据更新的notifyDataChanged函数 */
function multiConsumeSubjectFactory() {
    /**用来从store中获取最新数据的，并且下发给其他订阅者 */
    const consumeSubject = new rxjs_1.BehaviorSubject(0);
    function notifyDataChanged() {
        // 跳出react的render阶段
        setTimeout(() => {
            consumeSubject.next(Date.now());
        }, 0);
    }
    return {
        notifyDataChanged,
        consumeSubject,
    };
}
exports.multiConsumeSubjectFactory = multiConsumeSubjectFactory;
/**
 * 工厂函数，用来生成发送事件的dispatchEvent函数
 * @param store 实现了ArrayTPSLStore接口的实例
 * @returns dispatchEvent函数
 */
function multiDispatchEventFactory(store) {
    const eventSubject = new rxjs_1.Subject();
    function dispatchEvent(event) {
        // 跳出react的render阶段
        setTimeout(() => {
            eventSubject.next(event);
        }, 0);
    }
    eventSubject.subscribe({
        next: (e) => {
            if (!e) {
                return;
            }
            const { event, ids, orders } = e;
            switch (event) {
                case "update": {
                    store.update(ids, orders);
                    break;
                }
                case "remove": {
                    store.remove(ids, orders);
                    break;
                }
                case "refresh": {
                    store.refresh();
                    break;
                }
                default: {
                    break;
                }
            }
        },
        error: () => { }
    });
    return dispatchEvent;
}
exports.multiDispatchEventFactory = multiDispatchEventFactory;
